import { useMemo } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type {
    DmInboxStatus,
    DmNotification,
    DmNotificationPatientRow,
    DmResolvedNotification
} from '../../../../services/dmNotifications';
import { CollapsibleCell } from '../../shared/CollapsibleCell';
import { CountPill } from '../../shared/CountPill';
import { NotificationCard } from './NotificationCard';
import {
    sortNotificationCards,
    type NotificationCardSort,
    type NotificationCardSortKey
} from './notificationCardSort';
import { NotificationsAllReviewed } from './NotificationsAllReviewed';
import { NotificationSortControl } from './NotificationSortControl';
import { RecentlyResolvedSection } from './RecentlyResolvedSection';
import { useOpenNotificationCards } from './useOpenNotificationCards';

const CARD_CLASS = 'rounded-xl border border-gray-200';
const NO_CARDS: DmNotification[] = [];

interface PanelHeaderProps {
    embedded: boolean;
    openCount: number;
    showSort: boolean;
    sort: NotificationCardSort;
    onSelectSort: (key: NotificationCardSortKey) => void;
}

function PanelHeader({ embedded, openCount, showSort, sort, onSelectSort }: PanelHeaderProps) {
    if (embedded && !showSort) return null;
    return (
        <header className="flex flex-shrink-0 items-center gap-2 px-5 pb-3 pt-4">
            {!embedded && (
                <>
                    <h2 className="text-[13px] font-semibold text-gray-800">Notifications</h2>
                    <CountPill count={openCount} />
                </>
            )}
            {showSort && (
                <div className="ml-auto">
                    <NotificationSortControl sort={sort} onSelect={onSelectSort} />
                </div>
            )}
        </header>
    );
}

interface OpenCardListProps {
    notifications: readonly DmNotification[];
    leavingIds: ReadonlySet<string>;
    onReviewed: (notificationId: string) => Promise<void>;
}

// The gap is padding inside each collapsing wrapper, so it folds away with its notification.
function OpenCardList({ notifications, leavingIds, onReviewed }: OpenCardListProps) {
    return (
        <div className="flex flex-col px-5 pb-1">
            {notifications.map((notification) => (
                <CollapsibleCell key={notification.id} collapsed={leavingIds.has(notification.id)}>
                    <div className="pb-3">
                        <NotificationCard notification={notification} onReviewed={onReviewed} />
                    </div>
                </CollapsibleCell>
            ))}
        </div>
    );
}

interface OpenNotificationsPanelProps {
    patient: DmNotificationPatientRow;
    recentlyResolved: readonly DmResolvedNotification[];
    onNextPatient?: () => void;
    onBackToList?: () => void;
    // Inside a host tab: no card frame, and the tab label already carries the title and count.
    embedded?: boolean;
    // From Closed, a patient with nothing open shows its resolved notifications as the cards.
    status?: DmInboxStatus;
}

export function OpenNotificationsPanel({
    patient,
    recentlyResolved,
    onNextPatient,
    onBackToList,
    embedded = false,
    status = 'open'
}: OpenNotificationsPanelProps) {
    const cards = useOpenNotificationCards(patient);
    const showResolvedCards = status === 'closed' && cards.visible.length === 0;
    const resolvedCards = useMemo(
        () => (showResolvedCards ? sortNotificationCards(recentlyResolved, cards.sort) : NO_CARDS),
        [showResolvedCards, recentlyResolved, cards.sort]
    );
    const cardList = cards.visible.length > 0 ? cards.visible : resolvedCards;
    const hasCards = cardList.length > 0;
    return (
        <section
            aria-label="Notifications"
            className={`flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white font-inter ${embedded ? '' : CARD_CLASS}`}
        >
            <PanelHeader
                embedded={embedded}
                openCount={cards.openCount}
                showSort={hasCards}
                sort={cards.sort}
                onSelectSort={cards.selectSort}
            />
            {/* min-h-full lets the all-reviewed state centre itself above the resolved fold. */}
            <OverlayScrollArea
                className="min-h-0 flex-1"
                trackInsetPx={6}
                innerClassName="flex min-h-full flex-col"
            >
                {hasCards ? (
                    <OpenCardList
                        notifications={cardList}
                        leavingIds={cards.leavingIds}
                        onReviewed={cards.markLeaving}
                    />
                ) : (
                    <NotificationsAllReviewed
                        onNextPatient={onNextPatient}
                        onBackToList={onBackToList}
                    />
                )}
                {!showResolvedCards && <RecentlyResolvedSection notifications={recentlyResolved} />}
            </OverlayScrollArea>
        </section>
    );
}
